import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Logo from '../components/Logo';
import BurgerMenu from '../components/BurgerMenu';
import api from '../services/api';

interface Stats {
  users: {
    total: number;
    last7Days: number;
    last30Days: number;
  };
  lists: {
    total: number;
  };
  items: {
    total: number;
  };
  announcements: {
    active: number;
  };
}

interface Announcement {
  id: string;
  version: string;
  title: string;
  features: { icon: string; text: string }[];
  targetAudience: string;
  showFrom: string;
  showUntil: string | null;
  active: boolean;
  _count: {
    dismissals: number;
  };
}

export function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'stats' | 'announcements'>('stats');
  const [stats, setStats] = useState<Stats | null>(null);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form für neues Announcement
  const [formData, setFormData] = useState({
    version: '',
    title: '',
    features: [''],
    targetAudience: 'ADMINS_ONLY',
    showFrom: new Date().toISOString().slice(0, 16),
  });

  useEffect(() => {
    if (!user?.isAdmin) {
      navigate('/lists');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsRes, announcementsRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/announcements'),
      ]);
      console.log('Stats:', statsRes.data);
      console.log('Announcements:', announcementsRes.data);
      setStats(statsRes.data);
      setAnnouncements(announcementsRes.data);
    } catch (error: any) {
      console.error('Error loading admin data:', error);
      setError(error.response?.data?.error || 'Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAnnouncement = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/announcements', {
        ...formData,
        features: formData.features
          .filter((f) => f.trim())
          .map((text) => ({
            icon: '✨',
            text: text.trim(),
          })),
      });

      // Reset form
      setFormData({
        version: '',
        title: '',
        features: [''],
        targetAudience: 'ADMINS_ONLY',
        showFrom: new Date().toISOString().slice(0, 16),
      });

      loadData();
    } catch (error) {
      console.error('Error creating announcement:', error);
      alert('Fehler beim Erstellen des Announcements');
    }
  };

  const handleDeleteAnnouncement = async (id: string) => {
    if (!confirm('Announcement wirklich löschen?')) return;

    try {
      await api.delete(`/announcements/${id}`);
      loadData();
    } catch (error) {
      console.error('Error deleting announcement:', error);
      alert('Fehler beim Löschen des Announcements');
    }
  };

  if (!user?.isAdmin) return null;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg-secondary)' }}>
      <header style={{ 
        backgroundColor: 'var(--bg-primary)',
        padding: '1.5rem',
        borderBottom: '1px solid var(--border-color)'
      }}>
        <div className="container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Logo variant="dark" />
          <BurgerMenu currentPage="admin" />
        </div>
      </header>

      <main className="container" style={{ paddingTop: '2rem', paddingBottom: '2rem', maxWidth: '1200px', margin: '0 auto', padding: '2rem 1rem' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '30px', color: 'var(--text-primary)' }}>
          🔧 Admin Dashboard
        </h1>

        {loading && <div style={{ padding: '20px', color: 'var(--text-secondary)', textAlign: 'center' }}>Lade Daten...</div>}
        {error && (
          <div style={{ padding: '15px', marginBottom: '20px', backgroundColor: 'var(--danger-light)', color: 'var(--danger)', borderRadius: '8px', border: '1px solid var(--danger)' }}>
            ❌ {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', marginBottom: '30px', borderBottom: '2px solid var(--border-color)' }}>
          <button
            onClick={() => setActiveTab('stats')}
            style={{
              padding: '12px 24px',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'stats' ? '3px solid var(--secondary)' : '3px solid transparent',
              color: activeTab === 'stats' ? 'var(--secondary)' : 'var(--text-secondary)',
              fontWeight: activeTab === 'stats' ? '600' : '500',
              cursor: 'pointer',
              fontSize: '16px',
              transition: 'all 0.2s'
            }}
          >
            📊 Statistiken
          </button>
          <button
            onClick={() => setActiveTab('announcements')}
            style={{
              padding: '12px 24px',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'announcements' ? '3px solid var(--secondary)' : '3px solid transparent',
              color: activeTab === 'announcements' ? 'var(--secondary)' : 'var(--text-secondary)',
              fontWeight: activeTab === 'announcements' ? '600' : '500',
              cursor: 'pointer',
              fontSize: '16px',
              transition: 'all 0.2s'
            }}
          >
            📢 Announcements
          </button>
        </div>

        {activeTab === 'stats' && !loading && stats && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
            <StatCard
              title="Gesamt User"
              value={stats.users.total}
              subtitle={`+${stats.users.last7Days} letzte 7 Tage`}
              icon="👥"
            />
            <StatCard
              title="Neue User (30 Tage)"
              value={stats.users.last30Days}
              icon="🆕"
            />
            <StatCard title="Listen" value={stats.lists.total} icon="📋" />
            <StatCard title="Items" value={stats.items.total} icon="🛒" />
          </div>
        )}

        {activeTab === 'announcements' && !loading && (
          <div>
            <div className="card" style={{ marginBottom: '20px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px', color: 'var(--text-primary)' }}>
                Neues Announcement erstellen
              </h2>
              <form onSubmit={handleCreateAnnouncement} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                      Version
                    </label>
                    <input
                      type="text"
                      value={formData.version}
                      onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                      placeholder="v1.0.4"
                      style={{
                        width: '100%',
                        padding: '10px',
                        borderRadius: '8px',
                        border: '1px solid var(--border-color)',
                        backgroundColor: 'var(--bg-primary)',
                        color: 'var(--text-primary)',
                        fontSize: '14px'
                      }}
                      required
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                      Zielgruppe
                    </label>
                    <select
                      value={formData.targetAudience}
                      onChange={(e) => setFormData({ ...formData, targetAudience: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px',
                        borderRadius: '8px',
                        border: '1px solid var(--border-color)',
                        backgroundColor: 'var(--bg-primary)',
                        color: 'var(--text-primary)',
                        fontSize: '14px'
                      }}
                    >
                      <option value="ADMINS_ONLY">🔧 Nur Admins</option>
                      <option value="ALL">👥 Alle User</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                    Titel
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Was gibt's Neues?"
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid var(--border-color)',
                      backgroundColor: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: '14px'
                    }}
                    required
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                    Features
                  </label>
                  {formData.features.map((feature, index) => (
                    <div key={index} style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                      <input
                        type="text"
                        value={feature}
                        onChange={(e) => {
                          const newFeatures = [...formData.features];
                          newFeatures[index] = e.target.value;
                          setFormData({ ...formData, features: newFeatures });
                        }}
                        placeholder="Feature Beschreibung"
                        style={{
                          flex: 1,
                          padding: '10px',
                          borderRadius: '8px',
                          border: '1px solid var(--border-color)',
                          backgroundColor: 'var(--bg-primary)',
                          color: 'var(--text-primary)',
                          fontSize: '14px'
                        }}
                      />
                      {index === formData.features.length - 1 && (
                        <button
                          type="button"
                          onClick={() =>
                            setFormData({
                              ...formData,
                              features: [...formData.features, ''],
                            })
                          }
                          style={{
                            padding: '10px 20px',
                            borderRadius: '8px',
                            border: 'none',
                            backgroundColor: 'var(--bg-tertiary)',
                            color: 'var(--text-primary)',
                            cursor: 'pointer',
                            fontSize: '18px'
                          }}
                        >
                          +
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                    Anzeigen ab
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.showFrom}
                    onChange={(e) => setFormData({ ...formData, showFrom: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid var(--border-color)',
                      backgroundColor: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: '14px'
                    }}
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{ width: '100%' }}
                >
                  Announcement erstellen
                </button>
              </form>
            </div>

            <div className="card">
              <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px', color: 'var(--text-primary)' }}>
                Alle Announcements ({announcements.length})
              </h2>
              {announcements.length === 0 ? (
                <p style={{ color: 'var(--text-secondary)', padding: '20px', textAlign: 'center' }}>
                  Noch keine Announcements erstellt
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  {announcements.map((announcement) => (
                    <div
                      key={announcement.id}
                      style={{
                        border: '1px solid var(--border-color)',
                        borderRadius: '8px',
                        padding: '15px',
                        backgroundColor: 'var(--bg-secondary)'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '10px' }}>
                        <div>
                          <h3 style={{ fontWeight: '600', color: 'var(--text-primary)', marginBottom: '5px', fontSize: '16px' }}>
                            {announcement.version}: {announcement.title}
                          </h3>
                          <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                            {announcement.targetAudience === 'ADMINS_ONLY' ? '🔧 Nur Admins' : '👥 Alle User'}
                            {' • '}
                            {announcement._count.dismissals} Dismissals
                          </p>
                        </div>
                        <button
                          onClick={() => handleDeleteAnnouncement(announcement.id)}
                          className="btn btn-secondary"
                          style={{
                            padding: '6px 12px',
                            fontSize: '13px',
                            backgroundColor: '#FEE2E2',
                            color: '#DC2626',
                            border: '1px solid #FCA5A5'
                          }}
                        >
                          Löschen
                        </button>
                      </div>
                      <ul style={{ fontSize: '14px', color: '#4B5563', paddingLeft: '20px', marginBottom: '10px' }}>
                        {announcement.features.map((feature, idx) => (
                          <li key={idx} style={{ marginBottom: '4px' }}>
                            {feature.icon} {feature.text}
                          </li>
                        ))}
                      </ul>
                      <p style={{ fontSize: '12px', color: '#9CA3AF' }}>
                        Seit: {new Date(announcement.showFrom).toLocaleDateString('de-DE', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function StatCard({ title, value, subtitle, icon }: { title: string; value: number; subtitle?: string; icon: string; }) {
  return (
    <div className="card" style={{ textAlign: 'left' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
        <h3 style={{ fontSize: '14px', color: 'var(--text-secondary)', fontWeight: '500', margin: 0 }}>
          {title}
        </h3>
        <span style={{ fontSize: '36px' }}>{icon}</span>
      </div>
      <p style={{ fontSize: '36px', fontWeight: 'bold', color: 'var(--text-primary)', margin: '0 0 5px 0' }}>
        {value.toLocaleString('de-DE')}
      </p>
      {subtitle && (
        <p style={{ fontSize: '14px', color: 'var(--text-secondary)', margin: 0 }}>
          {subtitle}
        </p>
      )}
    </div>
  );
}
